import styled, { css } from 'styled-components';
import { Paper, Link, CheckboxLabelWrapper, LanguageSelect, Typography } from '../../design-system';
import { smallScreenSize, useIsSmallScreen } from '../../hooks';

const AuthWrapperBase = styled.div<{ isSmallScreen: boolean }>`
  display: grid;
  padding: 10rem 0;
  min-height: 100%;

  ${({ isSmallScreen }) =>
    isSmallScreen &&
    css`
      padding: 0;
    `}
`;

const AuthPaper = styled(Paper)`
  position: relative;

  &.MuiPaper-root {
    padding: 4.8rem 6rem;
    margin: auto;
  }

  & > *:last-child {
    padding-bottom: 4.8rem;
  }

  ${({ paperSize }) =>
    paperSize === 'mobile' &&
    css`
      display: grid;
      grid-auto-flow: row;
      grid-template-rows: max-content 1fr max-content;

      & > *:nth-child(1) {
        height: initial !important;

        & h4 {
          font-size: var(--font-size-5);
          line-height: var(--line-height-5);

          width: calc(100% - 4.4rem);
          text-align: left;
        }
      }

      & > *:last-child {
        padding-bottom: var(--spacing-6);
      }
    `}
`;

interface AuthWrapperProps {
  children: React.ReactNode;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }: AuthWrapperProps) => {
  const isSmallScreen = useIsSmallScreen();

  return (
    <AuthWrapperBase isSmallScreen={isSmallScreen}>
      <AuthPaper paperSize={isSmallScreen ? 'mobile' : 'default'}>{children}</AuthPaper>
    </AuthWrapperBase>
  );
};

export const AuthHeaderBase = styled.div`
  display: grid;
  grid-row-gap: 0.8rem;

  & > *:last-child {
    padding-bottom: 0 !important;
  }
`;

export const AuthHeaderSubTitle = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 0.6rem;
  justify-content: center;
`;

export const AuthCheckboxLabelWrapper = styled(CheckboxLabelWrapper)`
  margin: -0.8rem 0;
`;

export const AuthFormBase = styled.div`
  display: grid;
  grid-row-gap: 2.4rem;

  margin-top: 4rem;

  @media (max-width: ${smallScreenSize}px) {
    align-content: center;
    margin-top: 0;
  }
`;

export const AuthFooterBase = styled.div`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  justify-content: space-between;
  margin-top: 2.4rem;

  @media (max-width: ${smallScreenSize}px) {
    margin-top: 0;
  }
`;

export const AuthFooterTexts = styled.div`
  display: grid;
  grid-row-gap: 0.4rem;
`;

export const AuthFooterLink = styled(Link)`
  width: max-content;
  margin: auto;
`;

export const AuthFooterTypography = styled(Typography)`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const AuthFooterBorderLine = styled.div`
  width: 100%;
  height: 0.1rem;
  background-color: var(--gray-50);
  margin-top: 2.4rem;
`;

export const AuthFooterSocials = styled.div`
  display: grid;
  grid-row-gap: 1.2rem;
`;

const AuthFooterLanguageBase = styled.div<{ moveToTop?: boolean }>`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  margin-top: 2.4rem;

  ${({ moveToTop }) =>
    moveToTop &&
    css`
      margin-top: -2.4rem;
    `}

  @media (max-width: ${smallScreenSize}px) {
    margin-top: 0;
  }
`;

export const AuthFooterLanguage = ({ moveToTop = false }: { moveToTop?: boolean }) => {
  return (
    <AuthFooterLanguageBase moveToTop={moveToTop}>
      <LanguageSelect isButtonVariant />
    </AuthFooterLanguageBase>
  );
};
